<script setup>
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputSwitch from 'primevue/inputswitch';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';

import { useAdminStore } from '@/store/AdminStore';

const adminStore = useAdminStore();
const toast = useToast();
const confirm = useConfirm();

const sensorTypes = computed(() => adminStore.sensorTypes);
const isLoading = computed(() => adminStore.isLoading);

// Search/filter
const searchQuery = ref('');
const statusFilter = ref(null);

const filteredSensorTypes = computed(() => {
  let result = sensorTypes.value || [];

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      (s) =>
        s.name?.toLowerCase().includes(query) ||
        s.code?.toLowerCase().includes(query) ||
        s.unit?.toLowerCase().includes(query),
    );
  }

  if (statusFilter.value !== null) {
    result = result.filter((s) => s.enabled === statusFilter.value);
  }

  return result;
});

const sensorStats = computed(() => {
  const all = sensorTypes.value || [];
  return {
    total: all.length,
    enabled: all.filter((s) => s.enabled).length,
    withFields: all.filter((s) => s.fields?.length > 0).length,
  };
});

// Dialog state
const showDialog = ref(false);
const isEditing = ref(false);
const formData = ref({
  id: null,
  name: '',
  code: '',
  unit: '',
  icon: '',
  description: '',
  min_value: null,
  max_value: null,
  decimal_places: 2,
  fields: [],
  enabled: true,
});

// Field configuration for dynamic fields
const newField = ref({ name: '', type: 'text', label: '', required: false });
const fieldTypes = [
  { label: 'Text', value: 'text', icon: 'pi pi-align-left' },
  { label: 'Number', value: 'number', icon: 'pi pi-hashtag' },
  { label: 'Boolean', value: 'boolean', icon: 'pi pi-check-circle' },
  { label: 'Select', value: 'select', icon: 'pi pi-list' },
];

// Icon presets
const iconPresets = [
  { label: 'Temperature', value: 'pi pi-sun', color: '#ef4444' },
  { label: 'Humidity', value: 'pi pi-cloud', color: '#3b82f6' },
  { label: 'Pressure', value: 'pi pi-compass', color: '#8b5cf6' },
  { label: 'Light', value: 'pi pi-bolt', color: '#f59e0b' },
  { label: 'Motion', value: 'pi pi-directions', color: '#10b981' },
  { label: 'Sound', value: 'pi pi-volume-up', color: '#ec4899' },
  { label: 'Distance', value: 'pi pi-arrows-h', color: '#06b6d4' },
  { label: 'Speed', value: 'pi pi-forward', color: '#f97316' },
];

const resetForm = () => {
  formData.value = {
    id: null,
    name: '',
    code: '',
    unit: '',
    icon: '',
    description: '',
    min_value: null,
    max_value: null,
    decimal_places: 2,
    fields: [],
    enabled: true,
  };
  newField.value = { name: '', type: 'text', label: '', required: false };
  isEditing.value = false;
};

const openCreateDialog = () => {
  resetForm();
  showDialog.value = true;
};

const openEditDialog = (sensorType) => {
  formData.value = { ...sensorType, fields: sensorType.fields ? [...sensorType.fields] : [] };
  isEditing.value = true;
  showDialog.value = true;
};

const addField = () => {
  if (newField.value.name && newField.value.label) {
    formData.value.fields.push({ ...newField.value });
    newField.value = { name: '', type: 'text', label: '', required: false };
  }
};

const removeField = (index) => {
  formData.value.fields.splice(index, 1);
};

const generateCode = () => {
  if (formData.value.name && !formData.value.code) {
    formData.value.code = formData.value.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  }
};

const selectIconPreset = (preset) => {
  formData.value.icon = preset.value;
};

const saveSensorType = async () => {
  try {
    if (isEditing.value) {
      await adminStore.updateSensorType(formData.value.id, formData.value);
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Sensor type updated successfully',
        life: 3000,
      });
    } else {
      await adminStore.createSensorType(formData.value);
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Sensor type created successfully',
        life: 3000,
      });
    }
    showDialog.value = false;
    resetForm();
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save sensor type',
      life: 3000,
    });
  }
};

const confirmDelete = (sensorType) => {
  confirm.require({
    message: `Are you sure you want to delete "${sensorType.name}"? This cannot be undone.`,
    header: 'Delete Sensor Type',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await adminStore.deleteSensorType(sensorType.id);
        toast.add({
          severity: 'success',
          summary: 'Deleted',
          detail: 'Sensor type deleted successfully',
          life: 3000,
        });
      } catch {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete sensor type',
          life: 3000,
        });
      }
    },
  });
};

const toggleEnabled = async (sensorType) => {
  try {
    await adminStore.updateSensorType(sensorType.id, {
      ...sensorType,
      enabled: !sensorType.enabled,
    });
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Sensor type ${sensorType.enabled ? 'disabled' : 'enabled'}`,
      life: 3000,
    });
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update sensor type',
      life: 3000,
    });
  }
};

const getFieldTypeIcon = (type) => {
  const found = fieldTypes.find((ft) => ft.value === type);
  return found?.icon || 'pi pi-question';
};

onMounted(() => {
  adminStore.fetchSensorTypes(true);
});
</script>

<template>
  <div class="sensor-types-manager">
    <ConfirmDialog />

    <!-- Header -->
    <div class="manager-header">
      <div class="header-content">
        <div class="header-icon">
          <i class="pi pi-microchip"></i>
        </div>
        <div class="header-text">
          <h2>Sensor Types</h2>
          <p>Configure available sensor types and their data fields</p>
        </div>
      </div>

      <button class="add-btn" @click="openCreateDialog">
        <i class="pi pi-plus"></i>
        Add Sensor Type
      </button>
    </div>

    <!-- Stats Row -->
    <div class="stats-row">
      <div class="mini-stat">
        <span class="stat-value">{{ sensorStats.total }}</span>
        <span class="stat-label">Total Types</span>
      </div>
      <div class="mini-stat enabled">
        <span class="stat-value">{{ sensorStats.enabled }}</span>
        <span class="stat-label">Enabled</span>
      </div>
      <div class="mini-stat fields">
        <span class="stat-value">{{ sensorStats.withFields }}</span>
        <span class="stat-label">With Custom Fields</span>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <div class="search-box">
        <i class="pi pi-search"></i>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search sensor types..."
          class="search-input"
        />
      </div>

      <Dropdown
        v-model="statusFilter"
        :options="[
          { label: 'All Status', value: null },
          { label: 'Enabled', value: true },
          { label: 'Disabled', value: false },
        ]"
        optionLabel="label"
        optionValue="value"
        placeholder="Filter by status"
        class="status-filter"
      />
    </div>

    <!-- Sensor Types Grid -->
    <div v-if="!isLoading && filteredSensorTypes.length" class="sensor-grid">
      <div
        v-for="sensor in filteredSensorTypes"
        :key="sensor.id"
        class="sensor-card"
        :class="{ disabled: !sensor.enabled }"
      >
        <div class="card-header">
          <div class="sensor-icon" :class="{ 'no-icon': !sensor.icon }">
            <i :class="sensor.icon || 'pi pi-microchip'"></i>
          </div>
          <div class="sensor-info">
            <h3>{{ sensor.name }}</h3>
            <code>{{ sensor.code }}</code>
          </div>
          <div class="status-indicator" :class="{ active: sensor.enabled }">
            <span class="dot"></span>
            {{ sensor.enabled ? 'Active' : 'Disabled' }}
          </div>
        </div>

        <div class="card-body">
          <p v-if="sensor.description" class="description">{{ sensor.description }}</p>

          <div class="sensor-meta">
            <div class="meta-item" v-if="sensor.unit">
              <i class="pi pi-chart-line"></i>
              <span>Unit: <strong>{{ sensor.unit }}</strong></span>
            </div>
            <div class="meta-item" v-if="sensor.min_value !== null || sensor.max_value !== null">
              <i class="pi pi-sliders-h"></i>
              <span>Range: <strong>{{ sensor.min_value ?? '∞' }} - {{ sensor.max_value ?? '∞' }}</strong></span>
            </div>
            <div class="meta-item" v-if="sensor.decimal_places">
              <i class="pi pi-hashtag"></i>
              <span>Decimals: <strong>{{ sensor.decimal_places }}</strong></span>
            </div>
          </div>

          <div v-if="sensor.fields?.length" class="fields-preview">
            <div class="fields-label">
              <i class="pi pi-list"></i>
              Custom Fields ({{ sensor.fields.length }})
            </div>
            <div class="fields-tags">
              <span
                v-for="(field, idx) in sensor.fields.slice(0, 4)"
                :key="idx"
                class="field-tag"
                :class="{ required: field.required }"
              >
                <i :class="getFieldTypeIcon(field.type)"></i>
                {{ field.label }}
              </span>
              <span v-if="sensor.fields.length > 4" class="field-tag more">
                +{{ sensor.fields.length - 4 }} more
              </span>
            </div>
          </div>
        </div>

        <div class="card-actions">
          <button class="action-btn edit" v-tooltip.top="'Edit'" @click="openEditDialog(sensor)">
            <i class="pi pi-pencil"></i>
          </button>
          <button
            class="action-btn toggle"
            :class="{ disable: sensor.enabled }"
            v-tooltip.top="sensor.enabled ? 'Disable' : 'Enable'"
            @click="toggleEnabled(sensor)"
          >
            <i :class="sensor.enabled ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
          </button>
          <button
            class="action-btn delete"
            v-tooltip.top="'Delete'"
            @click="confirmDelete(sensor)"
          >
            <i class="pi pi-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else-if="isLoading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Loading sensor types...</p>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="empty-icon">
        <i class="pi pi-microchip"></i>
      </div>
      <h3>No sensor types found</h3>
      <p v-if="searchQuery || statusFilter !== null">Try adjusting your search or filters</p>
      <p v-else>Create your first sensor type to get started</p>
      <button class="add-btn" @click="openCreateDialog">
        <i class="pi pi-plus"></i>
        Add Sensor Type
      </button>
    </div>

    <!-- Create/Edit Dialog -->
    <Dialog
      v-model:visible="showDialog"
      :style="{ width: '680px' }"
      modal
      :closable="true"
      class="sensor-dialog"
    >
      <template #header>
        <div class="dialog-header">
          <div class="dialog-icon" :class="{ editing: isEditing }">
            <i :class="isEditing ? 'pi pi-pencil' : 'pi pi-plus'"></i>
          </div>
          <div>
            <h3>{{ isEditing ? 'Edit Sensor Type' : 'Create Sensor Type' }}</h3>
            <p>{{ isEditing ? 'Update sensor configuration' : 'Define a new sensor type' }}</p>
          </div>
        </div>
      </template>

      <div class="dialog-form">
        <!-- Basic Info Section -->
        <div class="form-section">
          <div class="section-header">
            <i class="pi pi-info-circle"></i>
            <span>Basic Information</span>
          </div>

          <div class="form-grid">
            <div class="form-field">
              <label>Name <span class="required">*</span></label>
              <InputText
                v-model="formData.name"
                placeholder="e.g., Temperature"
                @blur="generateCode"
              />
            </div>

            <div class="form-field">
              <label>Code <span class="required">*</span></label>
              <InputText v-model="formData.code" placeholder="e.g., temperature" />
              <small>Internal identifier (auto-generated from name)</small>
            </div>

            <div class="form-field">
              <label>Unit</label>
              <InputText v-model="formData.unit" placeholder="e.g., °C, %, hPa" />
            </div>

            <div class="form-field">
              <label>Decimal Places</label>
              <InputText
                v-model.number="formData.decimal_places"
                type="number"
                min="0"
                max="10"
              />
            </div>

            <div class="form-field span-2">
              <label>Description</label>
              <Textarea
                v-model="formData.description"
                rows="2"
                placeholder="Brief description of this sensor type"
              />
            </div>
          </div>
        </div>

        <!-- Icon Selection -->
        <div class="form-section">
          <div class="section-header">
            <i class="pi pi-palette"></i>
            <span>Icon</span>
          </div>

          <div class="icon-selector">
            <div class="icon-preview" :class="{ 'has-icon': formData.icon }">
              <i :class="formData.icon || 'pi pi-microchip'"></i>
            </div>
            <div class="icon-presets">
              <button
                v-for="preset in iconPresets"
                :key="preset.value"
                type="button"
                class="icon-preset"
                :class="{ selected: formData.icon === preset.value }"
                :style="{ '--preset-color': preset.color }"
                @click="selectIconPreset(preset)"
              >
                <i :class="preset.value"></i>
              </button>
            </div>
            <InputText
              v-model="formData.icon"
              placeholder="Or enter custom icon class"
              class="icon-input"
            />
          </div>
        </div>

        <!-- Value Constraints -->
        <div class="form-section">
          <div class="section-header">
            <i class="pi pi-sliders-h"></i>
            <span>Value Constraints</span>
          </div>

          <div class="form-grid cols-3">
            <div class="form-field">
              <label>Min Value</label>
              <InputText
                v-model.number="formData.min_value"
                type="number"
                placeholder="Optional"
              />
            </div>

            <div class="form-field">
              <label>Max Value</label>
              <InputText
                v-model.number="formData.max_value"
                type="number"
                placeholder="Optional"
              />
            </div>

            <div class="form-field enabled-toggle">
              <label>Status</label>
              <div class="toggle-wrapper">
                <InputSwitch v-model="formData.enabled" />
                <span>{{ formData.enabled ? 'Enabled' : 'Disabled' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Custom Fields Section -->
        <div class="form-section">
          <div class="section-header">
            <i class="pi pi-list"></i>
            <span>Custom Data Fields</span>
            <span class="field-count" v-if="formData.fields.length">
              {{ formData.fields.length }}
            </span>
          </div>

          <p class="section-description">
            Define additional fields that sensors of this type can capture
          </p>

          <!-- Existing Fields -->
          <div v-if="formData.fields.length" class="existing-fields">
            <div
              v-for="(field, index) in formData.fields"
              :key="index"
              class="field-item"
            >
              <div class="field-icon">
                <i :class="getFieldTypeIcon(field.type)"></i>
              </div>
              <div class="field-details">
                <span class="field-label">{{ field.label }}</span>
                <span class="field-meta">{{ field.name }} · {{ field.type }}</span>
              </div>
              <span v-if="field.required" class="required-badge">Required</span>
              <button type="button" class="remove-field" @click="removeField(index)">
                <i class="pi pi-times"></i>
              </button>
            </div>
          </div>

          <!-- Add Field Form -->
          <div class="add-field-form">
            <div class="field-inputs">
              <InputText
                v-model="newField.name"
                placeholder="field_name"
                class="field-name-input"
              />
              <InputText
                v-model="newField.label"
                placeholder="Display Label"
                class="field-label-input"
              />
              <Dropdown
                v-model="newField.type"
                :options="fieldTypes"
                optionLabel="label"
                optionValue="value"
                placeholder="Type"
                class="field-type-input"
              />
              <label class="required-checkbox">
                <input type="checkbox" v-model="newField.required" />
                Required
              </label>
              <button
                type="button"
                class="add-field-btn"
                @click="addField"
                :disabled="!newField.name || !newField.label"
              >
                <i class="pi pi-plus"></i>
                Add
              </button>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <Button label="Cancel" class="cancel-btn" @click="showDialog = false" />
          <Button
            :label="isEditing ? 'Update' : 'Create'"
            icon="pi pi-check"
            class="save-btn"
            @click="saveSensorType"
            :disabled="!formData.name || !formData.code"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.sensor-types-manager {
  animation: fadeIn 0.4s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.manager-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  box-shadow: 0 8px 32px rgba(16, 185, 129, 0.3);
}

.header-text h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--calid-text-primary, #fff);
  margin: 0;
}

.header-text p {
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
}

.add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
}

/* Stats */
.stats-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.mini-stat {
  flex: 1;
  min-width: 140px;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  text-align: center;
}

.mini-stat .stat-value {
  display: block;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--calid-text-primary, #fff);
}

.mini-stat .stat-label {
  font-size: 0.75rem;
  color: var(--calid-text-secondary, #94a3b8);
  text-transform: uppercase;
}

.mini-stat.enabled .stat-value {
  color: #10b981;
}

.mini-stat.fields .stat-value {
  color: #8b5cf6;
}

/* Filters */
.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--calid-text-secondary, #64748b);
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: var(--calid-text-primary, #fff);
  font-size: 0.9rem;
}

.search-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

:deep(.status-filter .p-dropdown) {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  min-width: 150px;
}

/* Sensor Grid */
.sensor-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.25rem;
}

.sensor-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.sensor-card:hover {
  border-color: rgba(16, 185, 129, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.sensor-card.disabled {
  opacity: 0.6;
}

.sensor-card.disabled:hover {
  border-color: rgba(255, 255, 255, 0.1);
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.sensor-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%);
  border: 1px solid rgba(16, 185, 129, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: #10b981;
  flex-shrink: 0;
}

.sensor-icon.no-icon {
  background: rgba(100, 116, 139, 0.1);
  border-color: rgba(100, 116, 139, 0.2);
  color: #64748b;
}

.sensor-info {
  flex: 1;
  min-width: 0;
}

.sensor-info h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--calid-text-primary, #fff);
  margin: 0 0 0.25rem;
}

.sensor-info code {
  font-size: 0.75rem;
  color: var(--calid-text-secondary, #94a3b8);
  background: rgba(255, 255, 255, 0.05);
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.status-indicator.active {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border-color: rgba(16, 185, 129, 0.3);
}

.status-indicator .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.card-body {
  padding: 1.25rem;
}

.description {
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.85rem;
  margin: 0 0 1rem;
  line-height: 1.5;
}

.sensor-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8rem;
  color: var(--calid-text-secondary, #94a3b8);
}

.meta-item i {
  font-size: 0.75rem;
  color: var(--calid-text-tertiary, #64748b);
}

.meta-item strong {
  color: var(--calid-text-primary, #fff);
}

/* Fields Preview */
.fields-preview {
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.fields-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.fields-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.field-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 6px;
  font-size: 0.75rem;
  color: #a78bfa;
}

.field-tag i {
  font-size: 0.65rem;
}

.field-tag.required {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
}

.field-tag.more {
  background: rgba(100, 116, 139, 0.1);
  border-color: rgba(100, 116, 139, 0.2);
  color: #94a3b8;
}

/* Card Actions */
.card-actions {
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.02);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: var(--calid-text-secondary, #94a3b8);
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.action-btn.edit:hover {
  background: rgba(0, 212, 255, 0.1);
  border-color: rgba(0, 212, 255, 0.4);
  color: #00d4ff;
}

.action-btn.toggle:hover {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.4);
  color: #fbbf24;
}

.action-btn.delete:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.4);
  color: #ef4444;
}

/* Empty/Loading States */
.empty-state,
.loading-state {
  padding: 4rem 2rem;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.03);
  border: 2px dashed rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon i {
  font-size: 2rem;
  color: var(--calid-text-secondary, #64748b);
}

.empty-state h3 {
  font-size: 1.25rem;
  color: var(--calid-text-primary, #fff);
  margin: 0 0 0.5rem;
}

.empty-state p {
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0 0 1.5rem;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid rgba(16, 185, 129, 0.2);
  border-top-color: #10b981;
  border-radius: 50%;
  margin: 0 auto 1rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Dialog Styles */
:deep(.sensor-dialog .p-dialog) {
  background: var(--calid-bg-secondary, #111827);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  max-height: 90vh;
  overflow: hidden;
}

:deep(.sensor-dialog .p-dialog-header) {
  background: transparent;
  border: none;
  padding: 1.5rem 1.5rem 0;
}

:deep(.sensor-dialog .p-dialog-content) {
  background: transparent;
  padding: 1.5rem;
  overflow-y: auto;
  max-height: calc(90vh - 200px);
}

:deep(.sensor-dialog .p-dialog-footer) {
  background: transparent;
  border: none;
  padding: 0 1.5rem 1.5rem;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.dialog-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
}

.dialog-icon.editing {
  background: linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%);
}

.dialog-header h3 {
  font-size: 1.25rem;
  color: var(--calid-text-primary, #fff);
  margin: 0;
}

.dialog-header p {
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
  font-size: 0.85rem;
}

/* Form Sections */
.dialog-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-section {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 1.25rem;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: var(--calid-text-primary, #fff);
}

.section-header i {
  color: #10b981;
}

.field-count {
  margin-left: auto;
  background: rgba(16, 185, 129, 0.2);
  color: #10b981;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
}

.section-description {
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.85rem;
  margin: -0.5rem 0 1rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-grid.cols-3 {
  grid-template-columns: 1fr 1fr 1fr;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-field.span-2 {
  grid-column: span 2;
}

.form-field label {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--calid-text-secondary, #94a3b8);
}

.form-field .required {
  color: #ef4444;
}

.form-field small {
  font-size: 0.7rem;
  color: var(--calid-text-tertiary, #64748b);
}

:deep(.dialog-form .p-inputtext),
:deep(.dialog-form .p-textarea) {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.625rem 0.875rem;
  color: var(--calid-text-primary, #fff);
  font-size: 0.9rem;
}

:deep(.dialog-form .p-inputtext:focus),
:deep(.dialog-form .p-textarea:focus) {
  border-color: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
}

:deep(.dialog-form .p-dropdown) {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

/* Icon Selector */
.icon-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
}

.icon-preview {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: rgba(100, 116, 139, 0.1);
  border: 2px dashed rgba(100, 116, 139, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #64748b;
  transition: all 0.3s;
}

.icon-preview.has-icon {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%);
  border: 2px solid rgba(16, 185, 129, 0.4);
  color: #10b981;
}

.icon-presets {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.icon-preset {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--preset-color, #94a3b8);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-preset:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: var(--preset-color);
  transform: scale(1.1);
}

.icon-preset.selected {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--preset-color);
  box-shadow: 0 0 0 2px var(--preset-color);
}

.icon-input {
  flex: 1;
  min-width: 200px;
}

/* Enabled Toggle */
.enabled-toggle .toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.toggle-wrapper span {
  color: var(--calid-text-primary, #fff);
  font-size: 0.9rem;
}

/* Existing Fields */
.existing-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.field-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.field-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a78bfa;
}

.field-details {
  flex: 1;
}

.field-label {
  display: block;
  font-weight: 500;
  color: var(--calid-text-primary, #fff);
  font-size: 0.9rem;
}

.field-meta {
  font-size: 0.75rem;
  color: var(--calid-text-secondary, #64748b);
}

.required-badge {
  padding: 0.125rem 0.5rem;
  background: rgba(245, 158, 11, 0.15);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 6px;
  font-size: 0.65rem;
  color: #fbbf24;
  text-transform: uppercase;
  font-weight: 600;
}

.remove-field {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-field:hover {
  background: rgba(239, 68, 68, 0.2);
}

/* Add Field Form */
.add-field-form {
  padding-top: 1rem;
  border-top: 1px dashed rgba(255, 255, 255, 0.1);
}

.field-inputs {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.field-name-input {
  width: 120px;
}

.field-label-input {
  flex: 1;
  min-width: 140px;
}

:deep(.field-type-input) {
  width: 110px;
}

.required-checkbox {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.85rem;
  cursor: pointer;
}

.required-checkbox input {
  accent-color: #10b981;
}

.add-field-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.625rem 1rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-field-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.add-field-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Dialog Footer */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.cancel-btn {
  padding: 0.75rem 1.25rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: var(--calid-text-secondary, #94a3b8);
}

.save-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 10px;
  color: white;
}

.save-btn:hover:not(:disabled) {
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
}

.save-btn:disabled {
  opacity: 0.6;
}

/* Responsive */
@media (max-width: 768px) {
  .sensor-grid {
    grid-template-columns: 1fr;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-grid.cols-3 {
    grid-template-columns: 1fr;
  }

  .form-field.span-2 {
    grid-column: span 1;
  }

  .field-inputs {
    flex-direction: column;
  }

  .field-name-input,
  .field-label-input,
  :deep(.field-type-input) {
    width: 100%;
  }
}
</style>
